import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";

import "react-toastify/dist/ReactToastify.css";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

// Breakpoint columns, shared with applyMinSizesToLayouts so a min can be capped to them.
const RGL_COLS = { lg: 12, sm: 4 };

import workspacesTabsStore from "@/store/workspacesTabsStore";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

// ACTIONS functions to pass to store.
import { getAllActionSummaryData } from "@/store/tradeDataStoreACTIONS";
import { getAllRequestInformation } from "@/store/requestInformationStoreACTIONS";
import { fetchLatestReleaseNotes } from "@/store/releaseNotesStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import ColouredScrollbars from "../ColouredScrollbar";

import {
  handleDragEnd,
  handleDragStart,
  onLayoutChange,
  updateStateFromStore,
} from "@/lib/reactGridLayout/reactGridLayoutHelper";
import { applyMinSizesToLayouts } from "@/lib/reactGridLayout/widgetMinSizeHelper";
import { returnCurrentTabStateJSON } from "@/lib/widgetHelperFunctions";
import { IActiveId, IState } from "@/lib/widgetsLayoutDir";
import EmptyWorkspaceMessage from "./EmptyWorkspaceMessage";
import GenerateAllWidgets from "./widgets/GenerateAllWidgets";

const ReactGridLayouts = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { theme } = useTheme();

  //Data from store:
  const workspacesTabs = workspacesTabsStore((state) => state.workspacesTabs);
  const tabNamesAndActive = workspacesTabsStore(
    (state) => state.tabNamesAndActive,
  );

  const [state, setState] = useState<IState>(returnCurrentTabStateJSON());

  // LQC-2517: persisted layouts carry whatever mins they were saved with, so re-apply
  // the canonical ones before RGL ever sees them.
  const normalisedState: IState = useMemo(
    () => ({
      ...state,
      layouts: applyMinSizesToLayouts(state.layouts, state.openWidgets, RGL_COLS),
    }),
    [state],
  );

  // Has fetched used to prevent react strick mode from calling this twice in local / DEV.
  const hasFetched = useRef(false);
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      async function loadAllTradeData() {
        await getAllActionSummaryData(getAccessTokenSilently);
      }
      loadAllTradeData();
      // LQC-637 — bulk-load the standalone request-information store once on app
      // load. SignalR (TradeInformationRequest → refreshRequestInformationForESID)
      // keeps it fresh per-esid thereafter.
      getAllRequestInformation(getAccessTokenSilently);
      fetchLatestReleaseNotes(getAccessTokenSilently);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // updating the RGL state is now on a useEffect hook and watching a toggle from the store to avoid over rendering.
    updateStateFromStore({ workspacesTabs, setState }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspacesTabs, tabNamesAndActive]);

  const onBreakpointChange = (breakpoint: string) => {
    setState((prevState) => ({
      ...prevState,
      currentBreakpoint: breakpoint,
    }));
  };

  // activeId used for DND functionality - Controlls by both 'handleDragEnd' and 'handleDragStart'
  const [activeId, setActiveId] = useState<IActiveId>({
    isActive: false,
    value: null,
    headerDragging: null,
    eventOver: "",
  });

  return (
    <div id="GridContainer" className="GridContainer h-full w-full text-black">
      <div className="flex h-full w-full bg-cubeBase-900 bg-cover dark:bg-cubeBaseDark-900 ">
        <div className="flex h-full w-full items-start justify-center">
          <div
            className={`mainWidgetBackground w-max-full r-2 flex h-[99%] w-[99%] rounded-xl border border-gray-200 bg-cubeBase-800 dark:border-black dark:bg-cubeBaseDark-800`}
          >
            {/* Create a swith to show info for an empty workspace VS a workspace that has widgets loaded. */}
            {normalisedState.layouts[normalisedState.currentBreakpoint].length ==
              0 && (
              <div className="EmptyWorkspaceMessageContainer absolute left-[calc(50%)] top-[calc(47%)]">
                <div className="z-2 flex">
                  <EmptyWorkspaceMessage />
                </div>
              </div>
            )}
            <div className="h-full w-full overflow-x-hidden">
              <DndContext
                // Off so dragging a token doesn't scroll the AG Grid it came from.
                autoScroll={false}
                onDragStart={(event) => {
                  handleDragStart({ event, setActiveId, state });
                }}
                onDragEnd={(event) => {
                  handleDragEnd({ event, activeId, setActiveId });
                }}
              >
                <ColouredScrollbars theme={theme}>
                  {/* ResponsiveReactGridLayout MUST be loaded. Cannot have a turnary stopping it on AG Charts will break */}

                  <ResponsiveReactGridLayout
                    layouts={normalisedState.layouts}
                    onBreakpointChange={onBreakpointChange}
                    onLayoutChange={(layout, layouts) =>
                      onLayoutChange({
                        layouts,
                        getAccessTokenSilently,
                      })
                    }
                    // breakpoint will need to match up with the screen size for side bar modal.
                    breakpoints={{ lg: 800, sm: 0 }}
                    cols={RGL_COLS}
                    rowHeight={30}
                    measureBeforeMount={false}
                    useCSSTransforms={state.mounted}
                    compactType={state.compactType}
                    preventCollision={!state.compactType}
                    draggableHandle=".widgetHandler"
                    draggableCancel=".hide-button-fromDrag"
                    resizeHandles={["se", "sw", "s", "e", "w"]} // no north as these are used for moving the widgets.
                  >
                    {GenerateAllWidgets({
                      state: normalisedState,
                      activeId,
                      theme,
                    })}
                  </ResponsiveReactGridLayout>
                </ColouredScrollbars>

                <DragOverlay>
                  {activeId.isActive ? (
                    <div
                      className={`drag-overlay flex w-[150px] justify-center ${activeId.headerDragging == "ESID" && "w-[150px]"} ${activeId.headerDragging == "Details" && "w-[220px]"}  bg-slate-100`}
                    >
                      {activeId.label ?? activeId.value}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactGridLayouts;
